import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as API from "services/api"
import * as StatusListHelpers from "utils/statusListHelpers"
import { errorToast } from "shared/toast"

const SLICE_NAME = "category"

const INITIAL_STATE = {
  category: {},
  groups: [],
  loading: true,
  submissionStatuses: [],
  taskForms: [],
  users: [],
}

export const loadCategoryFromSlug = createAsyncThunk(
  `${SLICE_NAME}/loadCategoryFromSlug`,
  async ({ categorySlug, options = {} }) => {
    const {
      loadSubmissionStatuses = false,
      loadTaskForms = false,
    } = options

    const payload = {}

    const categoryResponse = await API.getCategory({ categorySlug })

    if (categoryResponse.ok) {
      payload.category = categoryResponse.data
    } else {
      throw new Error("Unable to load solution data.")
    }

    if (loadSubmissionStatuses) {
      const submissionStatusesResponse = await API.getCategoryStatuses({ categorySlug })

      if (submissionStatusesResponse.ok) {
        payload.submissionStatuses = submissionStatusesResponse.data
      } else {
        throw new Error("Unable to load submission statuses.")
      }
    }

    if (loadTaskForms) {
      const taskFormsResponse = await API.getCategorySubforms({ categorySlug })

      if (taskFormsResponse.ok) {
        payload.taskForms = taskFormsResponse.data
      } else {
        throw new Error("Unable to task forms.")
      }
    }

    return payload
  },
)

export const categorySlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setGroups: (state, action) => {
      state.groups = action.payload
    },
    setUsers: (state, action) => {
      state.users = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCategoryFromSlug.pending, (state) => {
        state.loading = true
      })
      .addCase(loadCategoryFromSlug.fulfilled, (state, action) => {
        const { payload } = action
        const { category, submissionStatuses, taskForms } = payload

        state.category = category

        if (submissionStatuses) {
          state.submissionStatuses = submissionStatuses
        }

        if (taskForms) {
          state.taskForms = taskForms
        }

        state.loading = false
      })
      .addCase(loadCategoryFromSlug.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

export const {
  setGroups,
  setUsers,
} = categorySlice.actions

export const loading = (state) => state[SLICE_NAME].loading

export const categorySlug = (state) => state[SLICE_NAME].category.slug

export const submissionStatuses = (state) => state[SLICE_NAME].submissionStatuses

export const closedSubmissionStatus = (state) => StatusListHelpers.closedStatus({ statuses: submissionStatuses(state) })

export const closedSubmissionStatusName = (state) => closedSubmissionStatus(state).name

export const taskForms = (state) => state[SLICE_NAME].taskForms

export const activeTaskForms = (state) => taskForms(state).filter((taskForm) => taskForm.active)

export const groups = (state) => state[SLICE_NAME].groups

export const users = (state) => state[SLICE_NAME].users

export default categorySlice.reducer
